import { toast } from 'sonner';
import { v1, toResult } from '@/infrastructure/api/v1/client';
import type { ProjectCopyResult } from '@/components/dialogs/project-copy-dialog';

export async function copyProjectToUsers(
  projectId: string,
  targetEmails: string[],
  selectedSceneInstanceIds: string[],
  targetProjectName?: string,
): Promise<ProjectCopyResult> {
  try {
    toast.loading(`Copying project and stories to ${targetEmails.length} user${targetEmails.length > 1 ? 's' : ''}...`, { id: 'copy-project' });

    // The client id makes a repeat of this request answer with the first outcome, copying nothing more
    const { data, error } = await toResult(v1.POST('/api/v1/projects/{projectUniqueId}/user-copies', {
      params: { path: { projectUniqueId: projectId } },
      body: { uniqueId: crypto.randomUUID(), emails: targetEmails, sceneUniqueIds: selectedSceneInstanceIds, targetProjectName },
    }));

    if (error) {
      console.error('Error copying project:', error);
      toast.error(`Failed to copy project: ${error.message}`, { id: 'copy-project' });
      return { success: false };
    }

    const failedUsers = data.users.filter(r => !r.success);

    if (data.usersSucceeded > 0) {
      const successCount = data.usersSucceeded;
      const storyCount = selectedSceneInstanceIds.length;
      toast.success(
        `Successfully copied ${storyCount} ${storyCount === 1 ? 'story' : 'stories'} to ${successCount} user${successCount > 1 ? 's' : ''}`,
        { id: 'copy-project', duration: 5000 },
      );
    } else {
      toast.dismiss('copy-project');
    }

    // A missing target project surfaces inline in the dialog; every other per-target failure stays a toast
    const isNotFound = (r: (typeof failedUsers)[number]) => typeof r.error === 'string' && r.error.startsWith('No project named');
    const notFound = failedUsers.find(isNotFound);
    failedUsers.filter(r => !isNotFound(r)).forEach(failed => {
      console.error(`Failed for ${failed.email}:`, failed.error);
      toast.error(`${failed.email}: ${failed.error || 'Copy failed'}`, { duration: 6000 });
    });

    // Only the failed recipients go back, so a retry can't duplicate the copies that succeeded
    return {
      success: failedUsers.length === 0 && data.usersSucceeded > 0,
      projectNameError: notFound?.error ?? undefined,
      failedEmails: failedUsers.map(r => r.email).filter(Boolean),
    };
  } catch (error) {
    console.error('Error copying project:', error);
    toast.error(error instanceof Error ? error.message : 'Failed to copy project', { id: 'copy-project' });
    return { success: false };
  }
}
